import React, { useEffect, useState } from 'react';
import { Share2, Copy, Image as ImageIcon, X } from 'lucide-react';

/**
 * JustCreatedShareNudge
 * ---------------------
 * Quiet, dismissable one-shot banner that appears on Activity Detail
 * immediately after the user creates this activity. User-initiated — the
 * buttons hand off to the normal share/copy/flyer flows already on the page.
 * No auto-posting, no forced step.
 *
 * Triggered by sessionStorage['lk_just_created_activity'] === activityId.
 */
export function JustCreatedShareNudge({ activityId, onShare, onCopyLink, onFlyer }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const flag = sessionStorage.getItem('lk_just_created_activity');
      if (flag && flag === activityId) {
        setShow(true);
        // one-shot: clear immediately so a reload / re-nav doesn't resurface it
        sessionStorage.removeItem('lk_just_created_activity');
      }
    } catch { /* ignore */ }
  }, [activityId]);

  if (!show) return null;

  const act = (fn) => () => { try { fn && fn(); } catch { /* ignore */ } setShow(false); };

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: 'linear-gradient(135deg, #7A8F5B14 0%, #7A8F5B08 100%)',
        border: '1px dashed #7A8F5B55',
      }}
      data-testid="just-created-share-nudge"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
            Activity created. Share it?
          </p>
          <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
            Invite people you actually do things with. Totally optional.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShow(false)}
          className="p-1 -mr-1 -mt-1 rounded-full hover:opacity-70"
          aria-label="Dismiss"
          data-testid="just-created-dismiss"
        >
          <X className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={act(onShare)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-semibold"
          style={{ backgroundColor: '#7A8F5B', color: '#fff' }}
          data-testid="just-created-share"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button
          type="button"
          onClick={act(onCopyLink)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
          data-testid="just-created-copy"
        >
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
        <button
          type="button"
          onClick={act(onFlyer)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
          data-testid="just-created-flyer"
        >
          <ImageIcon className="w-3.5 h-3.5" /> Flyer
        </button>
      </div>
    </div>
  );
}
